import { useRef, useState, useEffect } from 'react';
import { useFamilyStore } from '@/store/familyStore';
import { useAuthStore } from '@/store/authStore';
import { TreeControls } from './components/TreeControls';
import { TreeCanvas } from './components/TreeCanvas';
import { TreeNodeDialog } from './components/TreeNodeDialog';
import { AddFirstMemberDialog } from './components/forms/AddFirstMemberDialog';
import { useTreeLayout } from './hooks/useTreeLayout';
import { useTreeNavigation } from './hooks/useTreeNavigation';
import { useTreeDimensions } from './hooks/useTreeDimensions';
import { FamilyMember } from '@/types';
import { TREE_CONFIG } from '@/config';

export function FamilyTree() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { members, addMember, updateMember, deleteMember } = useFamilyStore();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [selectedMember, setSelectedMember] = useState<FamilyMember | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'delete' | 'add-child'>('view');
  const [showAddFirst, setShowAddFirst] = useState(false);

  // Get container dimensions
  const dimensions = useTreeDimensions(containerRef);

  // Calculate tree layout
  const { nodePositions, contentDimensions } = useTreeLayout({
    members,
    nodeWidth: TREE_CONFIG.NODE_WIDTH,
    nodeHeight: TREE_CONFIG.NODE_HEIGHT,
    levelHeight: TREE_CONFIG.LEVEL_HEIGHT,
    frame: dimensions
  });

  // Handle navigation
  const {
    scale,
    position,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    handleButtonZoom,
    resetView
  } = useTreeNavigation({
    minScale: TREE_CONFIG.MIN_SCALE,
    maxScale: TREE_CONFIG.MAX_SCALE,
    containerDimensions: dimensions
  });

  // Set up wheel event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  const handleAddFirstMember = (name: string) => {
    const newMember = {
      id: crypto.randomUUID().replace(/[-]/g, ""),
      name,
      children: []
    };
    addMember(newMember);
    setShowAddFirst(false);
  };

  return (
    <div className="h-full flex flex-col">
      <div 
        ref={containerRef}
        className="relative flex-1 bg-gray-50 overflow-hidden border border-border rounded-lg mt-2"
        style={{ minHeight: 'calc(100vh - 15rem)' }}
      >
        <div 
          className="absolute inset-0"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <TreeControls
            onZoomIn={() => handleButtonZoom(true)}
            onZoomOut={() => handleButtonZoom(false)}
            onReset={resetView}
          />

          <TreeCanvas
            members={members}
            nodePositions={nodePositions}
            dimensions={dimensions}
            contentDimensions={contentDimensions}
            scale={scale}
            position={position}
            isDragging={isDragging}
            nodeWidth={TREE_CONFIG.NODE_WIDTH}
            nodeHeight={TREE_CONFIG.NODE_HEIGHT}
            onEditNode={(member) => {
              setSelectedMember(member);
              setDialogMode('edit');
            }}
            onDeleteNode={(member) => {
              setSelectedMember(member);
              setDialogMode('delete');
            }}
            onAddChild={(member) => {
              setSelectedMember(member);
              setDialogMode('add-child');
            }}
            onAddFirstMember={() => setShowAddFirst(true)}
            isAdmin={isAdmin}
          />
        </div>
      </div>

      {selectedMember && (
        <TreeNodeDialog
          member={selectedMember}
          onClose={() => {
            setSelectedMember(null);
            setDialogMode('view');
          }}
          mode={dialogMode}
          onAddChild={(names) => {
            names.forEach(name => {
              const newMember = {
                id: crypto.randomUUID().replace(/[-]/g, ""),
                name,
                parentId: selectedMember.id,
                children: []
              };
              addMember(newMember);
              updateMember(selectedMember.id, {
                children: [...selectedMember.children, newMember.id]
              });
            });
            setSelectedMember(null);
          }}
          onDelete={() => {
            deleteMember(selectedMember.id);
            setSelectedMember(null);
          }}
          onEdit={(name) => {
            updateMember(selectedMember.id, { name });
            setSelectedMember(null);
          }}
        />
      )}

      <AddFirstMemberDialog
        open={showAddFirst}
        onClose={() => setShowAddFirst(false)}
        onSubmit={handleAddFirstMember}
      />
    </div>
  );
}